import React, { useEffect, useMemo, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Field, reduxForm } from 'redux-form';
import { Button, Form, Label, FormGroup, Card, CardBody, CardHeader, CardFooter, CardTitle, Row, Col } from 'reactstrap';
import { mdiContentSave, mdiLoading } from '@mdi/js';
import Icon from '@mdi/react';
import DateTimePicker from '../../../components/DateTimePicker/DateTimePicker';
import { Device, PaymentSystem } from '../../../models';
import AccountPspCredentialsApi from '../../../services/rest/AccountPspCredentials';
import { credentialOptionLabel } from '../../../utils/credentialDisplay';

import './DeviceForm.css';

let DeviceForm = ({ isCreation, handleSubmit, pristine, submitting }) => {
    const { t }                 = useTranslation();
    const { accounts }          = useSelector((state) => state.accounts);
    const { payment_systems }   = useSelector((state) => state.payment_systems);
    const { isLoading, device } = useSelector( state => state.devices);
    const formValues = useSelector((state) => state.form?.deviceForm?.values || {});
    const [credentials, setCredentials] = useState([]);

    const selectedAccountId = formValues.account_id || device?.account_id;
    const selectedPaymentSystemId = formValues.payment_system_id || device?.payment_system_id;

    const payment_system_ids_for_credentials = useMemo(() => {
        if (!selectedPaymentSystemId || !payment_systems?.length) {
            return selectedPaymentSystemId ? [selectedPaymentSystemId] : [];
        }
        const ps = payment_systems.find((p) => p.id === selectedPaymentSystemId);
        if (!ps?.name) {
            return [selectedPaymentSystemId];
        }
        const sumup_names = [PaymentSystem.PAYMENT_SYSTEM_SUMUP, PaymentSystem.PAYMENT_SYSTEM_SUMUP_ONLINE];
        const wallee_names = [PaymentSystem.PAYMENT_SYSTEM_WALLEE, PaymentSystem.PAYMENT_SYSTEM_WALLEE_ONLINE];
        if (sumup_names.includes(ps.name)) {
            return payment_systems.filter((p) => sumup_names.includes(p.name)).map((p) => p.id);
        }
        if (wallee_names.includes(ps.name)) {
            return payment_systems.filter((p) => wallee_names.includes(p.name)).map((p) => p.id);
        }
        return [selectedPaymentSystemId];
    }, [selectedPaymentSystemId, payment_systems]);

    useEffect(() => {
        if (!selectedAccountId || payment_system_ids_for_credentials.length === 0) {
            setCredentials([]);
            return;
        }

        let cancelled = false;
        Promise.all(
            payment_system_ids_for_credentials.map((payment_system_id) =>
                AccountPspCredentialsApi.list({ account_id: selectedAccountId, payment_system_id })
            )
        )
            .then((results) => {
                if (cancelled) {
                    return;
                }
                const by_id = new Map();
                results.forEach((data) => {
                    (data.account_psp_credentials || []).forEach((credential) => {
                        if (credential?.id) {
                            by_id.set(credential.id, credential);
                        }
                    });
                });
                setCredentials([...by_id.values()]);
            })
            .catch(() => {
                if (!cancelled) {
                    setCredentials([]);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [selectedAccountId, payment_system_ids_for_credentials]);

    const selectedPaymentSystem = payment_systems?.find((ps) => ps.id === selectedPaymentSystemId);
    const currentPaymentSystemName = selectedPaymentSystem?.name || device?.payment_system?.name;

    const canSelectCredential = useMemo(() => (
        [PaymentSystem.PAYMENT_SYSTEM_SUMUP,
            PaymentSystem.PAYMENT_SYSTEM_SUMUP_ONLINE,
            PaymentSystem.PAYMENT_SYSTEM_WALLEE,
            PaymentSystem.PAYMENT_SYSTEM_WALLEE_ONLINE,
            PaymentSystem.PAYMENT_SYSTEM_STRIPE_ONLINE
        ].includes(currentPaymentSystemName)
    ), [currentPaymentSystemName]);

    return (
        <Form className="deviceForm" onSubmit={ handleSubmit }>
            <Card>
                <CardHeader>
                    <Row>
                        <Col>
                            <CardTitle className="big">{ isCreation ? t('devices.add_title') : t('devices.edit_title') }</CardTitle>
                        </Col>
                    </Row>
                </CardHeader>
                <CardBody>
                    <div className="">
                        <CardTitle>{ t('devices.general_infos') }</CardTitle>
                        <FormGroup>
                            <Label for="type">{ t('devices.type') }</Label>
                            <Field component="select" className="form-control" placeholder={ t('devices.type') } name="type" id="type" required>
                                { isCreation && <option>---</option> }
                                { Device.types().map(type => <option key={ type } value={ type }>{ t(`devices.types.${type}`) }</option>) }
                            </Field>
                        </FormGroup>

                        <FormGroup>
                            <Label for="name">{ t('devices.name') }</Label>
                            <Field component="input" className="form-control" placeholder={ t('devices.name') } name="name" id="name" required />
                        </FormGroup>

                        <FormGroup>
                            <Label for="account_id">{ t('devices.account') }</Label>
                            <Field component="select" className="form-control" placeholder={ t('devices.account_id') } name="account_id" id="account_id" required>
                                { isCreation && <option>---</option> }
                                { accounts && accounts.map(account => <option key={ account.id } value={ account.id }>{ account.name }</option>) }
                            </Field>
                        </FormGroup>
                        <FormGroup>
                            <Label for="start_at">{ t('devices.start_at') }</Label>
                            <Field
                                component={ DateTimePicker }
                                type="text"
                                name="start_at"
                                id="start_at"
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label for="stop_at">{ t('devices.stop_at') }</Label>
                            <Field
                                component={ DateTimePicker }
                                type="text"
                                name="stop_at"
                                id="stop_at"
                            />
                        </FormGroup>
                    </div>

                    <div className="mt-5">
                        <CardTitle>{ t('devices.integration_infos') }</CardTitle>

                        <FormGroup>
                            <Label for="identifier">{ t('devices.identifier') }</Label>
                            <Field component="input" className="form-control" placeholder={ t('devices.identifier') } name="identifier" id="identifier" required />
                        </FormGroup>

                        { !isCreation &&
                            <FormGroup>
                                <Label for="key_in">{ t('devices.key_in') }</Label>
                                <Field component="input" className="form-control" placeholder={ t('devices.key_in') } name="key_in" id="key_in" disabled required />
                            </FormGroup>
                        }

                        { !isCreation &&
                            <FormGroup>
                                <Label for="key_out">{ t('devices.key_out') }</Label>
                                <Field component="input" className="form-control" placeholder={ t('devices.key_out') } name="key_out" id="key_out" disabled required />
                            </FormGroup>
                        }
                    </div>

                    <div className="mt-5">
                        <CardTitle>{ t('devices.payment_system') }</CardTitle>
                        <FormGroup>
                            <Field component="select" className="form-control" placeholder={ t('devices.payment_system_id') } name="payment_system_id" id="payment_system_id" required>
                                { isCreation && <option>---</option> }
                                { payment_systems && payment_systems.map(payment_system => <option key={ payment_system.id } value={ payment_system.id }>{ payment_system.name }</option>) }
                            </Field>
                        </FormGroup>
                    </div>

                    {canSelectCredential && (
                        <div className="mt-4">
                            <CardTitle>{ t('devices.credential') }</CardTitle>
                            <FormGroup>
                                <Label for="credential_id">{ t('devices.credential_id') }</Label>
                                <Field
                                    component="select"
                                    className="form-control"
                                    name="credential_id"
                                    id="credential_id"
                                    required
                                >
                                    <option value="">---</option>
                                    {credentials.map((credential) => (
                                        <option key={ credential.id } value={ credential.id } title={credential.last_batch_check_detail || ''}>
                                            {credentialOptionLabel(credential, t)}
                                        </option>
                                    ))}
                                </Field>
                            </FormGroup>
                            <small className="text-muted">
                                { t('devices.credential_managed_on_account') }
                            </small>
                        </div>
                    )}
                    {!isCreation && device?.sumup_refresh_token_link && (
                        <div className="mt-2">
                            <a href={ device.sumup_refresh_token_link } target="_blank" rel="noopener noreferrer">
                                { t('devices.sumup_auth_refresh_token_link') }
                            </a>
                        </div>
                    )}
                    <div className="mt-5">
                        <CardTitle>{ `${t('devices.notify')} (${t('devices.comma_separated_emails')})` }</CardTitle>
                        <FormGroup>
                            <Field
                                component="textarea"
                                className="form-control"
                                name="notify"
                                id="notify"
                            />
                        </FormGroup>
                    </div>
                    <FormGroup check>
                        <Label check>
                            <Field
                                component="input"
                                type="checkbox"
                                name="can_refund"
                                id="can_refund"
                            /> { t('devices.can_refund') }
                        </Label>
                    </FormGroup>
                </CardBody>
                <CardFooter className="text-right">
                    <Button type="submit" color="success" disabled={ pristine || submitting || isLoading }>
                        { isLoading && <Icon path={ mdiLoading } size={ 1 } spin={ 1 } color="white" /> }
                        { !isLoading && <Icon path={ mdiContentSave } size={ 1 } color="white" /> }
                        &nbsp;{t("common.save")}
                    </Button>
                </CardFooter>
            </Card>
        </Form>
    );
}

DeviceForm = reduxForm({
    form: 'deviceForm',
    enableReinitialize: true,
})(DeviceForm)

DeviceForm = connect(
    state => ({
        initialValues: state.devices.device
    })
)(DeviceForm)

export default DeviceForm;
